import { useRef, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Briefcase, MapPin, ArrowUpRight, Sparkles } from 'lucide-react';
import { useAutoAdvance } from '@/lib/useAutoAdvance';

/**
 * CareerTimeline — an interactive stepper + detail-pane timeline.
 *
 * Layout is deliberately practical:
 *   • Desktop / tablet (md+) → left column = compact list of milestones
 *     with a live vertical rail; right column = a large detail card that
 *     updates on click/hover. No horizontal scrolling.
 *   • Mobile (< md) → the same list stacks vertically, and tapping any
 *     row expands its detail card inline underneath. Reads top-to-bottom
 *     with zero horizontal scroll.
 *
 * Data is drawn from Himanshu Kumar Tanty's public LinkedIn profile
 * (https://www.linkedin.com/in/anshuu/). Cards can be updated with real
 * dates and copy without touching the layout.
 */

// Ordered newest → oldest so the reader sees "where I am today" first
// and travels backward through the career. Dates + tenures match the
// public LinkedIn profile verbatim.
const timeline = [
    {
        range: 'Jun 2026 - Present',
        year: '2026',
        company: 'Wipro',
        role: 'Principal Consultant \u00b7 UX',
        location: 'Bengaluru · India',
        current: true,
        tags: ['IoT', 'Smart-Home', 'Design Systems'],
        note: 'Experience strategy, design systems and product initiatives across IoT and smart-home platforms.',
    },
    {
        range: 'May 2023 - Nov 2025',
        year: '2023',
        company: 'Harman Connected Services India Pvt. Ltd.',
        short: 'Harman',
        role: 'Lead UX Designer',
        location: 'Bengaluru · India',
        tags: ['Product Design', 'Prototypes', 'Cross-Functional'],
        note: 'Led customer-centred design from wireframes to shipped features, lifting usability and adoption with cross-functional teams.',
    },
    {
        range: 'Nov 2021 - Feb 2023',
        year: '2021',
        company: 'Engagedly Inc',
        short: 'Engagedly',
        role: 'Senior Product Designer',
        location: 'Remote · HR-tech SaaS',
        tags: ['SaaS', 'HR-Tech', 'Product'],
        note: 'Designed HR-tech SaaS at scale: dashboards, workflows and design-system contributions.',
    },
    {
        range: 'Jun 2021 - Nov 2021',
        year: '2021',
        company: 'Wipro',
        role: 'Senior UX Designer',
        location: 'Bengaluru · India',
        tags: ['Cloud', 'IoT', 'Enterprise'],
        note: 'Led feature workstreams across cloud and IoT programs; grew from designer to design lead.',
    },
    {
        range: 'Oct 2019 - Jul 2020',
        year: '2019',
        company: 'Wipro',
        role: 'UX Designer',
        location: 'Bengaluru · India',
        tags: ['Visual', 'Enterprise', 'Craft'],
        note: 'Joined Wipro\u2019s design practice, covering visual and interaction design across enterprise engagements.',
    },
    {
        range: 'Aug 2014 - Sep 2019',
        year: '2014',
        company: 'CAASP Labs Pvt. Ltd',
        short: 'CAASP Labs',
        role: 'UI / UX Designer',
        location: 'India',
        tags: ['SaaS', 'End-to-End', 'EdTech'],
        note: 'Owned end-to-end design for EDUCAASP, a school-management SaaS shipped across multiple institutions.',
    },
    {
        range: 'Feb 2012 - May 2014',
        year: '2012',
        company: 'Synchronous Software Solutions',
        short: 'Synchronous',
        role: 'Web & Graphic Designer',
        location: 'India',
        tags: ['HTML', 'CSS', 'JavaScript'],
        note: 'My foundation years: HTML, CSS, JavaScript and visual craft on live client websites.',
    },
];

// One chip on the horizontal stepper strip. Compact — shows year + short
// company. Active state fills lime; the current role always shows the pulse.
const StepChip = ({ item, i, active, onSelect }) => {
    const isActive = active === i;
    return (
        <button
            type="button"
            role="tab"
            aria-selected={isActive}
            onClick={() => onSelect(i)}
            onMouseEnter={() => onSelect(i)}
            data-cursor="hover"
            data-testid={`career-step-${i}`}
            className={`relative shrink-0 snap-start flex items-center gap-2 px-3.5 py-2 rounded-full border transition-colors focus:outline-none ${
                isActive
                    ? 'bg-[var(--accent)] text-[var(--accent-ink)] border-[var(--accent)] shadow-[0_10px_30px_-15px_var(--accent-glow)]'
                    : 'bg-[var(--surface)] text-[var(--ink-2)] border-[var(--line-2)] hover:border-[var(--line-3,#4a4a4d)] hover:text-[var(--ink)]'
            }`}
        >
            <span
                aria-hidden
                className={`relative flex items-center justify-center w-2 h-2 rounded-full ${
                    isActive
                        ? 'bg-[var(--accent-ink)]'
                        : item.current
                          ? 'bg-[var(--accent)]'
                          : 'bg-[var(--line-3,#4a4a4d)]'
                }`}
            >
                {item.current && !isActive && (
                    <span className="absolute inset-0 rounded-full bg-[var(--accent)] animate-ping opacity-60" />
                )}
            </span>
            <span className="font-mono-brand text-[10.5px] uppercase tracking-[0.18em] tabular-nums">
                {item.year}
            </span>
            <span className="font-sans-brand text-[12.5px] whitespace-nowrap">
                {item.short || item.company}
            </span>
            {item.current && (
                <span
                    className={`inline-block px-1.5 py-0.5 rounded-full font-mono-brand text-[9px] uppercase tracking-[0.14em] ${
                        isActive
                            ? 'bg-[var(--accent-ink)] text-[var(--accent)]'
                            : 'bg-[var(--accent)] text-[var(--accent-ink)]'
                    }`}
                >
                    Now
                </span>
            )}
        </button>
    );
};

// The big detail card that mirrors the active stepper chip.
const DetailPane = ({ item }) => (
    <AnimatePresence mode="wait">
        <motion.article
            key={item.company + item.range}
            initial={{ opacity: 0, y: 14 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -8 }}
            transition={{ duration: 0.4, ease: [0.2, 0.8, 0.2, 1] }}
            data-testid={`career-detail-${item.year}`}
            className={`relative rounded-[22px] border p-6 md:p-8 flex flex-col gap-5 overflow-hidden ${
                item.current
                    ? 'border-[var(--accent)] bg-[color-mix(in_oklab,var(--accent)_5%,var(--surface))] shadow-[0_30px_80px_-40px_var(--accent-glow)]'
                    : 'border-[var(--line)] bg-[var(--surface)]'
            }`}
        >
            {/* Meta strip */}
            <div className="flex items-center justify-between gap-3 flex-wrap">
                <span
                    className={`font-mono-brand text-[10.5px] uppercase tracking-[0.22em] tabular-nums ${
                        item.current ? 'text-[var(--accent)]' : 'text-[var(--ink-3)]'
                    }`}
                >
                    {item.range}
                </span>
                {item.current ? (
                    <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-[var(--accent)] text-[var(--accent-ink)] font-mono-brand text-[10px] uppercase tracking-[0.18em]">
                        <span className="w-1.5 h-1.5 rounded-full bg-[var(--accent-ink)] pulse-dot" />
                        Currently
                    </span>
                ) : (
                    <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full border border-[var(--line-2)] font-mono-brand text-[10px] uppercase tracking-[0.18em] text-[var(--ink-2)]">
                        <Sparkles size={10} className="text-[var(--accent)]" />
                        Chapter
                    </span>
                )}
            </div>

            {/* Big company + role */}
            <div>
                <h3 className="font-display-italic text-[36px] md:text-[48px] lg:text-[54px] leading-[1] text-[var(--ink)]">
                    {item.company.replace(/\.$/, '')}
                    <span className="text-[var(--accent)]">.</span>
                </h3>
                <div className="mt-3 flex items-center flex-wrap gap-x-4 gap-y-1.5">
                    <div className="flex items-center gap-1.5 font-sans-brand text-[14px] text-[var(--ink-2)]">
                        <Briefcase size={13} className="text-[var(--ink-3)]" />
                        <span className="text-[var(--ink)]">{item.role}</span>
                    </div>
                    {item.location && (
                        <div className="flex items-center gap-1.5 font-mono-brand text-[10.5px] uppercase tracking-[0.18em] text-[var(--ink-3)]">
                            <MapPin size={11} />
                            <span>{item.location}</span>
                        </div>
                    )}
                </div>
            </div>

            {/* Note */}
            <p className="font-sans-brand text-[14.5px] md:text-[15px] leading-[1.65] text-[var(--ink-2)]">
                {item.note}
            </p>

            {/* Focus tags */}
            {item.tags && item.tags.length > 0 && (
                <div className="flex flex-wrap gap-2">
                    {item.tags.map((t) => (
                        <span
                            key={t}
                            className="chip !py-1 !text-[10.5px]"
                        >
                            {t}
                        </span>
                    ))}
                </div>
            )}
        </motion.article>
    </AnimatePresence>
);

// Section header — matches other section framings on the site.
const SectionHead = () => (
    <div className="mb-8 md:mb-10 flex flex-col lg:flex-row lg:items-end lg:justify-between gap-5">
        <div className="max-w-3xl">
            <div className="font-mono-brand text-[10.5px] uppercase tracking-[0.22em] text-[var(--ink-3)] mb-3">
                Experience &nbsp;·&nbsp; 2012 to today
            </div>
            <h2 className="font-display leading-[1.02] text-[28px] sm:text-[34px] md:text-[42px] lg:text-[52px] text-[var(--ink)]">
                <span className="font-display-italic">Over a decade</span>,{' '}
                <span className="text-[var(--ink-2)]">one continuous line</span>
                <span className="text-[var(--accent)]">.</span>
            </h2>
            <p className="mt-3 font-sans-brand text-[14px] md:text-[15px] leading-[1.6] text-[var(--ink-2)]">
                From front-end craft in 2012 to design leadership across
                enterprise, AI and SaaS today. Tap any role to see more.
            </p>
        </div>

        <a
            href="https://www.linkedin.com/in/anshuu/"
            target="_blank"
            rel="noreferrer"
            data-testid="career-linkedin-cta"
            data-cursor="hover"
            className="inline-flex items-center gap-2 pl-4 pr-2 py-2 rounded-full border border-[var(--line-2)] text-[13px] font-medium text-[var(--ink)] hover:border-[var(--accent)] hover:text-[var(--accent)] transition-colors w-max shrink-0"
        >
            Full history on LinkedIn
            <span className="inline-flex items-center justify-center w-7 h-7 rounded-full bg-[var(--accent)] text-[var(--accent-ink)]">
                <ArrowUpRight size={14} />
            </span>
        </a>
    </div>
);

export const CareerTimeline = () => {
    // Default to the most recent (current) role — index 0
    const [active, setActive] = useState(0);
    const hoverRef = useRef(null);

    // Auto-cycle through every milestone so recruiters see the arc even if
    // they never touch a chip. Pauses on hover / focus.
    useAutoAdvance({
        interval: 4500,
        onTick: () => setActive((i) => (i + 1) % timeline.length),
        hoverRef,
    });

    return (
        <section
            id="career"
            ref={hoverRef}
            data-testid="career-timeline-section"
            className="relative py-10 md:py-14 px-4 md:px-6 border-t border-[var(--line)]"
        >
            <div className="mx-auto max-w-[1400px]">
                <SectionHead />

                {/* Horizontal chip strip — single scrollable row so chips
                    never orphan onto a second line. */}
                <div
                    role="tablist"
                    aria-label="Career milestones"
                    className="mb-5 md:mb-6 flex flex-wrap items-center gap-2 md:gap-2.5"
                >
                    {timeline.map((item, i) => (
                        <StepChip
                            key={i}
                            item={item}
                            i={i}
                            active={active}
                            onSelect={setActive}
                        />
                    ))}
                </div>

                {/* Big detail pane — spans full width below the strip */}
                <DetailPane item={timeline[active]} />
            </div>
        </section>
    );
};

export default CareerTimeline;
